import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionService } from '../../../services/subscriptionService';
import { authService } from '../../../services/authService';
import { showToast } from '../../../stores/uiStore';
import { handleApiError, api } from '../../../services/api';
import { useAuthStore } from '../../../stores/authStore';
import { PlanType, BillingCycle } from '../../../types';
import { getOperationErrorMessage } from '../../../config/errorMessages';
import { useSessionGate } from '../../../hooks/useSessionGate';
import { guardedNavigate } from '../../../lib/apiGuard/guardedNavigation';

export const useSubscription = () => {
  const { expired } = useSessionGate();
  return useQuery({
    queryKey: ['subscription'],
    queryFn: () => subscriptionService.getSubscription(),
    enabled: !expired,
  });
};

export const useCreateCheckout = () => {
  return useMutation({
    mutationFn: ({ plan, billingCycle }: { plan: PlanType; billingCycle: BillingCycle }) =>
      subscriptionService.createCheckout(plan, billingCycle),
    onSuccess: (response) => {
      if (response.success && response.data?.checkout_url) {
        api.post('/subscription/conversion-event', { event_type: 'checkout_started', source: 'settings' }).catch(() => {});
        guardedNavigate(response.data.checkout_url);
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('create'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useChangeSubscription = () => {
  const queryClient = useQueryClient();
  const { updatePlan, updateUser } = useAuthStore();

  return useMutation({
    mutationFn: (plan: PlanType) => subscriptionService.changeSubscription(plan),
    onSuccess: (response) => {
      if (response.success && response.data) {
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.invalidateQueries({ queryKey: ['user'] });
        updatePlan(response.data.plan);
        authService.getProfile().then((profileRes) => {
          if (profileRes.success && profileRes.data) {
            updateUser(profileRes.data);
          }
        }).catch(() => {});
        showToast.success(response.data.message);
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('update'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars?: { reason_category?: string; reason_detail?: string }) =>
      subscriptionService.cancelSubscription(vars?.reason_category, vars?.reason_detail),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        showToast.success(response.data?.message || 'サブスクリプションを解約しました');
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('submit'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useBillingPortal = () => {
  return useMutation({
    mutationFn: () => subscriptionService.getBillingPortalUrl(),
    onSuccess: (response) => {
      if (response.success && response.data?.portal_url) {
        guardedNavigate(response.data.portal_url);
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('fetch'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

